import { changeI18nLanguage, i18n } from '@/app/localization/i18n';
import {
  Languages,
  SettingStore,
  SettingStoreUserValues,
  TABLE_PRESET_SNAPSHOT_KEYS,
  TableSettings,
  Theme,
} from './types';
import type { DashboardPreset } from '@/entities/dashboards/model/api/types';
import { normalizeUiSettings } from './utils/normalizeUiSettings';
import { applyThemeToDocument, getInitialTheme } from './utils/theme';
import { createStore } from '@/shared/lib/store';
import { DEFAULT_MAIN_COLUMNS } from '@/entities/conversations';
import { DEFAULT_API_HOST, setApiHostGetter } from '@/shared/lib';

/** Префикс ключа пресета в `tables`: `<tableName>:<uuid>` */
const PRESET_KEY_SEPARATOR = ':';

/** Собирает полный идентификатор пресета по имени таблицы и uuid. */
const buildPresetId = (tableName: string, uuid: string): string => `${tableName}${PRESET_KEY_SEPARATOR}${uuid}`;

/** Проверяет, что ключ в `tables` относится к указанной таблице как пресет. */
const isPresetKeyOfTable = (key: string, tableName: string): boolean =>
  key.startsWith(`${tableName}${PRESET_KEY_SEPARATOR}`);

/**
 * Возвращает только те поля TableSettings, которые входят в снимок пресета.
 * См. {@link TABLE_PRESET_SNAPSHOT_KEYS}.
 */
const pickPresetSnapshot = (settings: TableSettings | undefined): Partial<TableSettings> => {
  if (!settings) return {};
  const snapshot: Partial<TableSettings> = {};
  for (const key of TABLE_PRESET_SNAPSHOT_KEYS) {
    if (settings[key] !== undefined) {
      (snapshot as Record<string, unknown>)[key] = settings[key];
    }
  }
  return snapshot;
};

/**
 * Возвращает объект, обнуляющий все поля снимка ({@link TABLE_PRESET_SNAPSHOT_KEYS}).
 * Нужен, чтобы при замене снимка старые значения не «протекали» поверх нового.
 */
const clearedPresetSnapshot = (): Partial<TableSettings> => {
  const cleared: Partial<TableSettings> = {};
  for (const key of TABLE_PRESET_SNAPSHOT_KEYS) {
    cleared[key] = undefined;
  }
  return cleared;
};

/**
 * Таблица уже в слое вида — в пресете или во внешней выборке, а не в личном виде пользователя.
 *
 * Слой отмечает любая из трёх записей: активный пресет, отложенный личный вид, пресет под выборкой.
 * Одного отложенного вида мало: у пресета, ставшего активным без него (сохранён из текущего вида
 * или пришёл из настроек прежних версий), его нет, и повторный вход в выборку (переход по второй
 * выборке; в разработке — двойной эффект монтирования) принял бы её за личный вид: отложил бы саму
 * выборку, и она вернулась бы в отбор при снятии пресета.
 */
const isInViewLayer = (settings: TableSettings): boolean =>
  settings.activePresetId !== undefined ||
  settings.previousView !== undefined ||
  settings.previousPresetId !== undefined;

/**
 * Убирает фильтр с указанным идентификатором из набора фильтров вида.
 * Пустой набор в состоянии таблицы не хранится — вместо него `undefined`.
 */
const dropFilter = (filters: TableSettings['filters'], filterId: string): TableSettings['filters'] => {
  const rest = (filters ?? []).filter((filter) => filter.id !== filterId);
  return rest.length > 0 ? rest : undefined;
};

/** Язык интерфейса по умолчанию — берётся из i18n или 'ru' */
export const DEFAULT_LANGUAGE: Languages = (i18n.language as Languages) || 'ru';

/**
 * Начальные значения пользовательских настроек.
 * Применяются при первом входе пользователя или при отсутствии сохранённых данных.
 */
export const initialUserValues: SettingStoreUserValues = {
  dialogsPage: {
    layout: 'visualizer',
    attributes: [],
    mainColumns: DEFAULT_MAIN_COLUMNS,
    hiddenDataTabs: [],
  },
  tables: {},
  sidebar: {
    activePanel: 'navigation',
    copilotView: 'chat',
    copilotActiveChatId: null,
    copilotWidth: 480,
  },
  dashboardQuality: {},
  dashboardPresets: {},
};

/**
 * Глобальный стор настроек приложения.
 *
 * ## Персистенция
 *
 * В localStorage (`'global-settings'`, версия 1) сохраняются **только глобальные поля**:
 * `theme`, `language`, `apiHost`, `serviceToken`.
 *
 * Пользовательские настройки (`settings[userId]`) хранятся **только на бэкенде**
 * (`/profile/ui-settings`) и загружаются в стор при авторизации через `initUserSettings`.
 * Синхронизация изменений — через `useUiSettingsSync`.
 *
 * ## Структура
 *
 * ### Глобальные поля (персистируются в localStorage)
 * - `theme` — тема оформления; глобальная, так как может быть задана до авторизации
 * - `apiHost` — переопределённый URL бэкенда; при отсутствии используется `DEFAULT_API_HOST`
 * - `language` — язык интерфейса (`'ru'` | `'en'`)
 * - `serviceToken` — сервисный токен авторизации
 *
 * ### Пользовательские поля (`settings[userId]: SettingStoreUserValues`, только in-memory)
 * - `dialogsPage` — настройки страницы диалогов: макет, видимые атрибуты, колонки
 * - `tables` — настройки таблиц по имени: фильтры, сортировка, поиск, offset
 * - `sidebar` — настройки боковой панели: активная панель, режим копилота, активный чат, ширина
 * - `dashboardPresets` — пользовательские дашборды-пресеты и применённый из них
 *
 * ## Хуки-обёртки
 * - {@link useSettingsActions} — только экшены без подписки на state
 * - {@link useSettings} — полный доступ к настройкам текущего пользователя + все экшены
 */
export const useSettingsStore = createStore<SettingStore>(
  'settings',
  (set, getState) => ({
    settings: {},
    theme: getInitialTheme(),
    apiHost: undefined,
    language: undefined,
    serviceToken: undefined,
    setApiHost: (apiHost) => {
      set({ apiHost });
      setApiHostGetter(() => getState().apiHost || DEFAULT_API_HOST);
    },
    resetApiHost: () => {
      set({ apiHost: undefined });
      setApiHostGetter(() => getState().apiHost || DEFAULT_API_HOST);
    },
    setServiceToken: (token) => {
      set({ serviceToken: token });
    },
    clearServiceToken: () => {
      set({ serviceToken: undefined });
    },
    setSidebarSettings: (userId, settings) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id] ?? { ...initialUserValues };
        state.settings[id] = {
          ...userSettings,
          sidebar: { ...userSettings.sidebar, ...settings },
        };
      });
    },
    setDialogsSettings: (userId, settings) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id] ?? { ...initialUserValues };
        state.settings[id] = {
          ...userSettings,
          dialogsPage: { ...userSettings.dialogsPage, ...settings },
        };
      });
    },
    setLanguage: (language) => {
      changeI18nLanguage(language);
      set({ language });
    },
    setTheme: (theme) => {
      applyThemeToDocument(theme);
      set({ theme });
    },
    setTableSettings: (userId, name, settings) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id] ?? { ...initialUserValues };
        state.settings[id] = {
          ...userSettings,
          tables: {
            ...userSettings.tables,
            [name]: { ...userSettings.tables[name], ...settings },
          },
        };
      });
    },
    saveTablePreset: (userId, tableName, presetName, selectionSource) => {
      // uuid генерируем снаружи set, чтобы вернуть его наружу из экшена
      const presetId = buildPresetId(tableName, crypto.randomUUID());
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id] ?? { ...initialUserValues };
        const activeSettings = userSettings.tables[tableName];
        const snapshot = pickPresetSnapshot(activeSettings);
        state.settings[id] = {
          ...userSettings,
          tables: {
            ...userSettings.tables,
            // Сразу делаем созданный пресет активным
            [tableName]: { ...activeSettings, ...snapshot, activePresetId: presetId },
            [presetId]: { ...snapshot, name: presetName, selectionSource },
          },
        };
      });
      return presetId;
    },
    updateTablePreset: (userId, tableName, presetId) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id];
        if (!userSettings) return;
        const existing = userSettings.tables[presetId];
        // Пресет не найден или не относится к указанной таблице — игнорируем
        if (!existing || !isPresetKeyOfTable(presetId, tableName)) return;
        const snapshot = pickPresetSnapshot(userSettings.tables[tableName]);
        state.settings[id] = {
          ...userSettings,
          tables: {
            ...userSettings.tables,
            // Перезаписываем только вид: отбрасываем прежний снимок и накладываем новый.
            // Остальное описание пресета (имя, источник выборки, закрепление в меню, любое
            // будущее поле) переживает запись — переносить его поимённо не требуется.
            [presetId]: { ...existing, ...clearedPresetSnapshot(), ...snapshot },
          },
        };
      });
    },
    applyTablePreset: (userId, tableName, presetId) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id];
        if (!userSettings) return;
        const preset = userSettings.tables[presetId];
        if (!preset || !isPresetKeyOfTable(presetId, tableName)) return;
        const snapshot = pickPresetSnapshot(preset);
        const active = userSettings.tables[tableName] ?? {};
        // При входе в слой из личного вида откладываем его в previousView — снятие пресета вернёт
        // таблицу к нему. При переключении между слоями отложенный вид не перезаписываем, чтобы
        // возврат вёл к личному виду, а не к предыдущему слою.
        const previousView = isInViewLayer(active) ? active.previousView : pickPresetSnapshot(active);
        state.settings[id] = {
          ...userSettings,
          tables: {
            ...userSettings.tables,
            [tableName]: {
              ...active,
              // Удаляем у активной записи все поля снимка, которых нет в пресете —
              // иначе старые значения «протекут» поверх пресета
              ...clearedPresetSnapshot(),
              ...snapshot,
              activePresetId: presetId,
              previousView,
              // Выбор пресета заменяет слой целиком: пресет, поверх которого была открыта
              // выборка, к новому слою отношения не имеет
              previousPresetId: undefined,
            },
          },
        };
      });
    },
    enterTableView: (userId, tableName, view, options) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id] ?? { ...initialUserValues };
        const active = userSettings.tables[tableName] ?? {};
        // Резервируем личный вид только при входе в слой из личного вида и только по запросу
        // (options.backup): повторный вход по той же/другой выборке и откат донастройки (reset)
        // previousView не трогают.
        const previousView =
          options?.backup && !isInViewLayer(active) ? pickPresetSnapshot(active) : active.previousView;
        // Пресет, поверх которого открыта выборка, запоминаем отдельно от личного вида: снятие
        // выборки вернёт таблицу к нему, а снятие самого пресета — к личному виду за ним.
        // Вход в выборку поверх выборки прежнюю привязку не теряет.
        const previousPresetId = options?.backup
          ? (active.activePresetId ?? active.previousPresetId)
          : active.previousPresetId;
        state.settings[id] = {
          ...userSettings,
          tables: {
            ...userSettings.tables,
            [tableName]: {
              ...active,
              ...view,
              // Слой выборки не является именованным пресетом — активный пресет снимаем
              activePresetId: undefined,
              previousView,
              previousPresetId,
            },
          },
        };
      });
    },
    deleteTablePreset: (userId, presetId) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id];
        if (!userSettings) return;
        if (!userSettings.tables[presetId]) return;
        const nextTables: Record<string, TableSettings> = {};
        for (const [key, value] of Object.entries(userSettings.tables)) {
          if (key === presetId) continue;
          // Ссылки на удалённый пресет сбрасываем: и активность, и точку возврата слоя выборки —
          // иначе снятие выборки вело бы к несуществующему виду
          const isActive = value.activePresetId === presetId;
          const isReturnPoint = value.previousPresetId === presetId;
          if (!isActive && !isReturnPoint) {
            nextTables[key] = value;
            continue;
          }
          nextTables[key] = {
            ...value,
            activePresetId: isActive ? undefined : value.activePresetId,
            previousPresetId: isReturnPoint ? undefined : value.previousPresetId,
          };
        }
        state.settings[id] = { ...userSettings, tables: nextTables };
      });
    },
    renameTablePreset: (userId, presetId, name) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id];
        if (!userSettings) return;
        const existing = userSettings.tables[presetId];
        if (!existing) return;
        state.settings[id] = {
          ...userSettings,
          tables: {
            ...userSettings.tables,
            [presetId]: { ...existing, name },
          },
        };
      });
    },
    setTablePresetPinned: (userId, presetId, pinned) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id];
        if (!userSettings) return;
        const existing = userSettings.tables[presetId];
        if (!existing) return;
        state.settings[id] = {
          ...userSettings,
          tables: {
            ...userSettings.tables,
            // Незакреплённый пресет хранится без признака — записи нет, значит не закреплён
            [presetId]: { ...existing, pinnedToMenu: pinned || undefined },
          },
        };
      });
    },
    clearActiveTablePreset: (userId, tableName) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id];
        if (!userSettings) return;
        const active = userSettings.tables[tableName];
        if (!active) return;
        // Снимать нечего: активного пресета нет. Слой внешней выборки этим путём не снимается —
        // у него свой выход (`exitTableSelection`), иначе открытая поверх пресета выборка
        // пропадала бы вместе с ним, едва открывшись.
        if (active.activePresetId === undefined) return;
        const hasPreviousView = active.previousView !== undefined;
        if (hasPreviousView) {
          // Возврат к отложенному личному виду: обнуляем поля снимка и накладываем previousView,
          // затем очищаем сам отложенный вид и активный пресет
          state.settings[id] = {
            ...userSettings,
            tables: {
              ...userSettings.tables,
              [tableName]: {
                ...active,
                ...clearedPresetSnapshot(),
                ...active.previousView,
                activePresetId: undefined,
                previousView: undefined,
                previousPresetId: undefined,
              },
            },
          };
          return;
        }
        // Пресет без отложенного вида (создан до появления слоёв) — просто снимаем активность,
        // вид таблицы остаётся прежним
        state.settings[id] = {
          ...userSettings,
          tables: {
            ...userSettings.tables,
            [tableName]: { ...active, activePresetId: undefined, previousPresetId: undefined },
          },
        };
      });
    },
    exitTableSelection: (userId, tableName, selectionFilterId) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id];
        if (!userSettings) return;
        const active = userSettings.tables[tableName];
        if (!active) return;
        const previousPreset = active.previousPresetId ? userSettings.tables[active.previousPresetId] : undefined;
        const writeTable = (settings: TableSettings) => {
          state.settings[id] = {
            ...userSettings,
            tables: { ...userSettings.tables, [tableName]: settings },
          };
        };
        if (previousPreset) {
          // Выборка была открыта поверх пресета — возвращаем его вид и активность;
          // отложенный личный вид остаётся за пресетом и вернётся при его снятии
          writeTable({
            ...active,
            ...clearedPresetSnapshot(),
            ...pickPresetSnapshot(previousPreset),
            activePresetId: active.previousPresetId,
            previousPresetId: undefined,
          });
          return;
        }
        if (active.previousView !== undefined) {
          // Возврат к отложенному личному виду. Выборку вычищаем и из него: в состояниях,
          // накопленных до появления полноценного снятия, она могла осесть в личном виде,
          // и возврат к нему вернул бы её же обратно в отбор.
          writeTable({
            ...active,
            ...clearedPresetSnapshot(),
            ...active.previousView,
            filters: dropFilter(active.previousView.filters, selectionFilterId),
            activePresetId: undefined,
            previousView: undefined,
            previousPresetId: undefined,
          });
          return;
        }
        // Возвращать нечего (залипшая выборка без слоя) — убираем её из действующего отбора,
        // остальную донастройку пользователя оставляем как есть
        writeTable({
          ...active,
          filters: dropFilter(active.filters, selectionFilterId),
          activePresetId: undefined,
          previousPresetId: undefined,
        });
      });
    },
    setDashboardQualitySettings: (userId, settings) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id] ?? { ...initialUserValues };
        state.settings[id] = {
          ...userSettings,
          dashboardQuality: { ...userSettings.dashboardQuality, ...settings },
        };
      });
    },
    createDashboardPreset: (userId, name, charts) => {
      // uuid генерируем снаружи set, чтобы вернуть его наружу из экшена
      const presetId = crypto.randomUUID();
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id] ?? { ...initialUserValues };
        const section = userSettings.dashboardPresets;
        state.settings[id] = {
          ...userSettings,
          dashboardPresets: {
            ...section,
            presets: {
              ...section.presets,
              [presetId]: { id: presetId, name, pinned: false, charts },
            },
            // Созданный дашборд сразу показан: пользователь только что описал, что хочет видеть
            activePresetId: presetId,
          },
        };
      });
      return presetId;
    },
    updateDashboardPreset: (userId, presetId, changes) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id];
        if (!userSettings) return;
        const existing = userSettings.dashboardPresets.presets?.[presetId];
        if (!existing) return;
        state.settings[id] = {
          ...userSettings,
          dashboardPresets: {
            ...userSettings.dashboardPresets,
            presets: {
              ...userSettings.dashboardPresets.presets,
              [presetId]: {
                ...existing,
                name: changes.name ?? existing.name,
                charts: changes.charts ?? existing.charts,
              },
            },
          },
        };
      });
    },
    applyDashboardPreset: (userId, presetId) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id];
        if (!userSettings) return;
        if (!userSettings.dashboardPresets.presets?.[presetId]) return;
        state.settings[id] = {
          ...userSettings,
          dashboardPresets: { ...userSettings.dashboardPresets, activePresetId: presetId },
        };
      });
    },
    clearActiveDashboardPreset: (userId) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id];
        if (!userSettings) return;
        if (userSettings.dashboardPresets.activePresetId === undefined) return;
        state.settings[id] = {
          ...userSettings,
          dashboardPresets: { ...userSettings.dashboardPresets, activePresetId: undefined },
        };
      });
    },
    deleteDashboardPreset: (userId, presetId) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id];
        if (!userSettings) return;
        const section = userSettings.dashboardPresets;
        if (!section.presets?.[presetId]) return;
        const nextPresets: Record<string, DashboardPreset> = {};
        for (const [key, value] of Object.entries(section.presets)) {
          if (key !== presetId) nextPresets[key] = value;
        }
        state.settings[id] = {
          ...userSettings,
          dashboardPresets: {
            presets: nextPresets,
            // Удалённый пресет показывать не из чего — страница возвращается к стандартному дашборду
            activePresetId: section.activePresetId === presetId ? undefined : section.activePresetId,
          },
        };
      });
    },
    setDashboardPresetPinned: (userId, presetId, pinned) => {
      set((state) => {
        const id = String(userId);
        const userSettings = state.settings[id];
        if (!userSettings) return;
        const existing = userSettings.dashboardPresets.presets?.[presetId];
        if (!existing) return;
        state.settings[id] = {
          ...userSettings,
          dashboardPresets: {
            ...userSettings.dashboardPresets,
            presets: {
              ...userSettings.dashboardPresets.presets,
              [presetId]: { ...existing, pinned },
            },
          },
        };
      });
    },
    initUserSettings: (userId, settings) => {
      set((state) => {
        const id = String(userId);
        // Сохранённый набор мог быть снят прежним составом настроек: непонятные значения
        // отбрасываем, чтобы давний профиль не ломал экран и не терял остальные настройки
        const stored = normalizeUiSettings(settings);
        // Инициализируем настройки пользователя из бэкенда, дополняя отсутствующие поля значениями по умолчанию
        state.settings[id] = {
          ...initialUserValues,
          ...stored,
          dialogsPage: {
            ...initialUserValues.dialogsPage,
            ...(stored.dialogsPage ?? {}),
          },
          sidebar: {
            ...initialUserValues.sidebar,
            ...(stored.sidebar ?? {}),
          },
          tables: stored.tables ?? {},
          dashboardQuality: {
            ...initialUserValues.dashboardQuality,
            ...(stored.dashboardQuality ?? {}),
          },
          dashboardPresets: {
            ...initialUserValues.dashboardPresets,
            ...(stored.dashboardPresets ?? {}),
          },
        };
      });
    },
  }),
  {
    localStorage: true,
    storageName: 'global-settings',
    version: 1,
    partialize: (state: SettingStore) => ({
      theme: state.theme,
      language: state.language,
      apiHost: state.apiHost,
      serviceToken: state.serviceToken,
    }),
    onRehydrateStorage: () => (state: unknown) => {
      const s = state as { theme?: Theme; language?: Languages } | null;
      if (!s || typeof s !== 'object') return;

      if (s.language) {
        changeI18nLanguage(s.language);
      }
      if (s.theme) {
        applyThemeToDocument(s.theme);
      }
    },
  },
);

/**
 * Хук, возвращающий только экшены стора настроек.
 * Не подписывается на изменения state — безопасен для использования
 * в компонентах, которым не нужна реактивность на значения.
 */
export const useSettingsActions = () => ({
  setLanguage: useSettingsStore((store) => store.setLanguage),
  setTheme: useSettingsStore((store) => store.setTheme),
  setDialogsSettings: useSettingsStore((store) => store.setDialogsSettings),
  setSidebarSettings: useSettingsStore((store) => store.setSidebarSettings),
  setTableSettings: useSettingsStore((store) => store.setTableSettings),
  saveTablePreset: useSettingsStore((store) => store.saveTablePreset),
  updateTablePreset: useSettingsStore((store) => store.updateTablePreset),
  applyTablePreset: useSettingsStore((store) => store.applyTablePreset),
  enterTableView: useSettingsStore((store) => store.enterTableView),
  deleteTablePreset: useSettingsStore((store) => store.deleteTablePreset),
  renameTablePreset: useSettingsStore((store) => store.renameTablePreset),
  setTablePresetPinned: useSettingsStore((store) => store.setTablePresetPinned),
  clearActiveTablePreset: useSettingsStore((store) => store.clearActiveTablePreset),
  exitTableSelection: useSettingsStore((store) => store.exitTableSelection),
  setDashboardQualitySettings: useSettingsStore((store) => store.setDashboardQualitySettings),
  createDashboardPreset: useSettingsStore((store) => store.createDashboardPreset),
  updateDashboardPreset: useSettingsStore((store) => store.updateDashboardPreset),
  applyDashboardPreset: useSettingsStore((store) => store.applyDashboardPreset),
  clearActiveDashboardPreset: useSettingsStore((store) => store.clearActiveDashboardPreset),
  deleteDashboardPreset: useSettingsStore((store) => store.deleteDashboardPreset),
  setDashboardPresetPinned: useSettingsStore((store) => store.setDashboardPresetPinned),
  initUserSettings: useSettingsStore((store) => store.initUserSettings),
  setApiHost: useSettingsStore((store) => store.setApiHost),
  resetApiHost: useSettingsStore((store) => store.resetApiHost),
});
